// data-fit LLC · DecisionFlow · ADMIN Stage
// ScenarioSetBuilder.jsx · DDL: R20 · API: v1.27 · S247 Phase 10
// Auth: useAuth() direct — ARCH-2 compliant
// Key constraints:
//   comp_admin — full access (create, add definitions, approve)
//   finance    — read only
//   Actor separation: approver must differ from created_by (NN-36)
//   is_runnable = active + approved + has P50/P75/P90
// Response envelopes:
//   GET  /scenario-sets        → { scenario_sets: [...], count }
//   POST /scenario-sets        → _ss_response (no definitions key)
//   POST /{id}/definitions     → { scenario_set_id, definitions_written, definitions }
//   POST /{id}/approve         → _ss_response
// Closes: OI-S206-12
// S247 Phase 10:
//   - OI-AMENDMENT-UI-BEGIN-MODAL: AmendmentBeginModal fires before approve
//   - OI-AMENDMENT-UI-COMMIT-ON-SAVE: amendment committed after successful approve
//   - governed_entity_type = "scenario_definition"
//   - Create + definitions are NOT individually governed (the approval step is the governed mutation)
//   - Props added: wizardSessionId

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../auth/AuthContext.jsx'
import AmendmentBeginModal from '../admin/AmendmentBeginModal.jsx'
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react'

const API_BASE = '/api/v1'
const GOVERNED_ENTITY_TYPE = 'scenario_definition'

const DEFAULT_DEFINITIONS = [
  { scenario_code: 'P50', scenario_name: '50th Percentile', percentile_label: 'P50', attainment_multiplier: '1.00', credit_factor: '', budget_comparison_basis: 'ARR', notes: '' },
  { scenario_code: 'P75', scenario_name: '75th Percentile', percentile_label: 'P75', attainment_multiplier: '1.25', credit_factor: '', budget_comparison_basis: 'ARR', notes: '' },
  { scenario_code: 'P90', scenario_name: '90th Percentile', percentile_label: 'P90', attainment_multiplier: '1.50', credit_factor: '', budget_comparison_basis: 'ARR', notes: '' },
]

const INITIAL_SET_FORM = { fiscal_period_id: '', scenario_set_code: '', scenario_set_name: '', effective_date: '', version: '1', is_active: true }

function fmt(val) { if (val == null) return '—'; return String(val) }
function fmtDate(val) { if (!val) return '—'; return String(val).slice(0, 10) }
function fmtDateTime(val) { if (!val) return '—'; try { return new Date(val).toLocaleString() } catch { return String(val) } }
function getErrorMessage(payload) {
  return payload?.detail?.error?.message || payload?.error?.message || payload?.message || payload?.detail || 'Unexpected error.'
}

async function commitAmendment(amendmentId) {
  if (!amendmentId) return;
  try {
    const res = await fetch(`${API_BASE}/amendments/${amendmentId}/commit`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) { const b = await res.json().catch(() => ({})); console.warn('[Amendment] Commit failed:', b?.error?.message ?? res.status); }
  } catch (e) { console.warn('[Amendment] Commit network error:', e.message); }
}

function SetStatusBadge({ set }) {
  if (set.is_runnable) return <span className="inline-flex items-center gap-1 rounded-full bg-[rgb(var(--color-success))]/10 px-2 py-0.5 text-tok-xs font-medium text-[rgb(var(--color-success))]">✓ Runnable</span>
  if (set.approved_at) return <span className="inline-flex items-center gap-1 rounded-full bg-[rgb(var(--color-info))]/10 px-2 py-0.5 text-tok-xs font-medium text-[rgb(var(--color-info))]">✓ Approved</span>
  return <span className="inline-flex items-center gap-1 rounded-full bg-[rgb(var(--color-warning))]/10 px-2 py-0.5 text-tok-xs font-medium text-[rgb(var(--color-warning))]">⏳ Draft</span>
}

function DefCodePills({ codes }) {
  const required = ['P50', 'P75', 'P90']
  return (
    <div className="flex gap-1 flex-wrap">
      {required.map((code) => {
        const has = codes.includes(code)
        return (
          <span key={code} className={`inline-flex items-center rounded px-1.5 py-0.5 text-tok-xs font-mono font-semibold ${has ? 'bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]' : 'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]'}`}>
            {code}
          </span>
        )
      })}
    </div>
  )
}

function DefinitionsEditor({ definitions, onChange }) {
  const inputClass = 'w-full rounded border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-2 py-1 text-tok-xs text-[rgb(var(--color-text))]'
  const setField = (index, field, value) => {
    const next = definitions.map((d, i) => i === index ? { ...d, [field]: value } : d)
    onChange(next)
  }
  return (
    <div className="flex flex-col gap-3">
      {definitions.map((def, i) => (
        <div key={def.scenario_code} className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="rounded bg-[rgb(var(--color-surface-muted))] px-2 py-0.5 text-tok-xs font-mono font-semibold text-[rgb(var(--color-text))]">{def.scenario_code}</span>
            <span className="text-tok-xs text-[rgb(var(--color-text))]">{def.scenario_name}</span>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <label className="flex flex-col gap-0.5 text-tok-xs font-medium text-[rgb(var(--color-text))]">Percentile Label<input type="text" value={def.percentile_label} onChange={(e) => setField(i, 'percentile_label', e.target.value)} className={inputClass} /></label>
            <label className="flex flex-col gap-0.5 text-tok-xs font-medium text-[rgb(var(--color-text))]">Attainment Multiplier *<input type="number" step="0.01" min="0.01" value={def.attainment_multiplier} onChange={(e) => setField(i, 'attainment_multiplier', e.target.value)} className={inputClass} /></label>
            <label className="flex flex-col gap-0.5 text-tok-xs font-medium text-[rgb(var(--color-text))]">Credit Factor<input type="number" step="0.01" min="0.01" placeholder="optional" value={def.credit_factor} onChange={(e) => setField(i, 'credit_factor', e.target.value)} className={inputClass} /></label>
            <label className="flex flex-col gap-0.5 text-tok-xs font-medium text-[rgb(var(--color-text))]">Budget Basis<select value={def.budget_comparison_basis} onChange={(e) => setField(i, 'budget_comparison_basis', e.target.value)} className={inputClass}><option value="ARR">ARR</option><option value="TCV">TCV</option><option value="MRR">MRR</option></select></label>
          </div>
        </div>
      ))}
    </div>
  )
}

function CreateWizard({ fiscalPeriodId, fiscalPeriodLabel, periods, onCreated, onCancel, amendmentId }) {
  const [step, setStep]               = useState(1)
  const [setForm, setSetForm]         = useState({ ...INITIAL_SET_FORM, fiscal_period_id: fiscalPeriodId ? String(fiscalPeriodId) : '' })
  const [definitions, setDefs]        = useState(DEFAULT_DEFINITIONS)
  const [createdSet, setCreatedSet]   = useState(null)
  const [submitting, setSub]          = useState(false)
  const [error, setError]             = useState(null)
  const [defsWritten, setDefsWritten] = useState(null)

  const inputClass = 'w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))]'
  const labelClass = 'flex flex-col gap-1 text-tok-xs font-medium text-[rgb(var(--color-text))]'

  const handleCreateSet = async () => {
    setError(null)
    if (!setForm.scenario_set_code.trim()) { setError('scenario_set_code is required.'); return }
    if (!setForm.scenario_set_name.trim()) { setError('scenario_set_name is required.'); return }
    if (!setForm.fiscal_period_id)         { setError('fiscal_period_id is required.'); return }
    if (!setForm.effective_date)           { setError('effective_date is required.'); return }
    const body = {
      fiscal_period_id:  Number(setForm.fiscal_period_id),
      scenario_set_code: setForm.scenario_set_code.trim(),
      scenario_set_name: setForm.scenario_set_name.trim(),
      effective_date:    setForm.effective_date,
      version:           Number(setForm.version) || 1,
      is_active:         setForm.is_active,
    }
    if (amendmentId) body.amendment_id = amendmentId
    setSub(true)
    try {
      const res = await fetch(`${API_BASE}/scenario-sets`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      const payload = await res.json().catch(() => ({}))
      if (!res.ok) { setError(getErrorMessage(payload)); return }
      setCreatedSet(payload); setStep(2)
    } catch (e) { setError(e.message || 'Network error.') }
    finally { setSub(false) }
  }

  const handleAddDefinitions = async () => {
    setError(null)
    const id = createdSet?.scenario_set_id
    if (!id) { setError('No scenario set ID — restart the wizard.'); return }
    for (const d of definitions) {
      if (!d.attainment_multiplier || Number(d.attainment_multiplier) <= 0) { setError(`attainment_multiplier for ${d.scenario_code} must be > 0.`); return }
    }
    const body = {
      definitions: definitions.map((d) => ({
        scenario_code: d.scenario_code, scenario_name: d.scenario_name,
        percentile_label: d.percentile_label || d.scenario_code,
        attainment_multiplier: d.attainment_multiplier,
        credit_factor: d.credit_factor !== '' ? d.credit_factor : undefined,
        budget_comparison_basis: d.budget_comparison_basis,
        notes: d.notes || null,
      })),
    }
    setSub(true)
    try {
      const res = await fetch(`${API_BASE}/scenario-sets/${id}/definitions`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      const payload = await res.json().catch(() => ({}))
      if (!res.ok) { setError(getErrorMessage(payload)); return }
      setDefsWritten(payload.definitions_written); setStep(3)
    } catch (e) { setError(e.message || 'Network error.') }
    finally { setSub(false) }
  }

  const stepLabels = ['Set Details', 'P50/P75/P90', 'Review']

  return (
    <div className="rounded-xl border border-[rgb(var(--color-primary))]/20 bg-[rgb(var(--color-primary))]/5 p-4 flex flex-col gap-4">
      {amendmentId && (
        <div className="rounded-lg bg-[rgb(var(--color-warning))]/10 border border-[rgb(var(--color-warning))]/30 px-3 py-2 text-tok-xs text-[rgb(var(--color-warning))]">
          ⚠ Amendment open — this scenario set creation is governed. Complete to commit.
        </div>
      )}
      <div className="flex items-center gap-1">
        {stepLabels.map((label, idx) => {
          const n = idx + 1; const isActive = step === n; const isComplete = step > n
          return (
            <React.Fragment key={label}>
              <div className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-tok-xs font-medium ${isActive ? 'bg-[rgb(var(--color-primary))] text-white' : isComplete ? 'bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]' : 'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]'}`}>
                <span>{isComplete ? '✓' : n}</span><span>{label}</span>
              </div>
              {idx < stepLabels.length - 1 && <div className="h-px w-4 bg-[rgb(var(--color-border))]" />}
            </React.Fragment>
          )
        })}
      </div>
      {error && <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{error}</div>}
      {step === 1 && (
        <div className="flex flex-col gap-3">
          <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Scenario Set Details</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className={labelClass}>Fiscal Period *<select value={setForm.fiscal_period_id} onChange={(e) => setSetForm((f) => ({ ...f, fiscal_period_id: e.target.value }))} className={inputClass}><option value="">Select period</option>{periods.map((p) => <option key={p.fiscal_period_id} value={p.fiscal_period_id}>{p.period_label ?? p.period_code}</option>)}</select></label>
            <label className={labelClass}>Set Code *<input type="text" value={setForm.scenario_set_code} onChange={(e) => setSetForm((f) => ({ ...f, scenario_set_code: e.target.value }))} className={inputClass} /></label>
            <label className={labelClass}>Set Name *<input type="text" value={setForm.scenario_set_name} onChange={(e) => setSetForm((f) => ({ ...f, scenario_set_name: e.target.value }))} className={inputClass} /></label>
            <label className={labelClass}>Effective Date *<input type="date" value={setForm.effective_date} onChange={(e) => setSetForm((f) => ({ ...f, effective_date: e.target.value }))} className={inputClass} /></label>
            <label className={labelClass}>Version<input type="number" min="1" step="1" value={setForm.version} onChange={(e) => setSetForm((f) => ({ ...f, version: e.target.value }))} className={inputClass} /></label>
            <label className="flex items-center gap-2 text-tok-xs font-medium text-[rgb(var(--color-text))]"><input type="checkbox" checked={setForm.is_active} onChange={(e) => setSetForm((f) => ({ ...f, is_active: e.target.checked }))} className="h-4 w-4" />Active</label>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={handleCreateSet} disabled={submitting} className="rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50">{submitting ? 'Creating…' : 'Create Set →'}</button>
            <button type="button" onClick={onCancel} disabled={submitting} className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50">Cancel</button>
          </div>
        </div>
      )}
      {step === 2 && (
        <div className="flex flex-col gap-3">
          <div>
            <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">P50 / P75 / P90 Definitions</h3>
            <p className="text-tok-xs text-[rgb(var(--color-text))] mt-0.5">Set created: <span className="font-mono">{createdSet?.scenario_set_code}</span> · All three percentiles required for approval.</p>
          </div>
          <DefinitionsEditor definitions={definitions} onChange={setDefs} />
          <div className="flex gap-2">
            <button type="button" onClick={handleAddDefinitions} disabled={submitting} className="rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50">{submitting ? 'Saving…' : 'Save Definitions →'}</button>
          </div>
        </div>
      )}
      {step === 3 && (
        <div className="flex flex-col gap-3">
          <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Review & Done</h3>
          <div className="rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-3 grid gap-2 text-tok-sm">
            <div className="flex justify-between"><span className="text-[rgb(var(--color-text))]">Set Code</span><span className="font-mono text-[rgb(var(--color-text))]">{createdSet?.scenario_set_code}</span></div>
            <div className="flex justify-between"><span className="text-[rgb(var(--color-text))]">Set Name</span><span className="text-[rgb(var(--color-text))]">{createdSet?.scenario_set_name}</span></div>
            <div className="flex justify-between"><span className="text-[rgb(var(--color-text))]">Definitions Written</span><span className="text-[rgb(var(--color-text))]">{defsWritten ?? '—'}</span></div>
            <div className="flex justify-between"><span className="text-[rgb(var(--color-text))]">Status</span><span className="text-[rgb(var(--color-warning))]">Draft — pending approval</span></div>
          </div>
          <p className="text-tok-xs text-[rgb(var(--color-text))]">A <strong>different comp_admin</strong> must approve this set (NN-36 actor separation). The set will appear in the list below — use the Approve button there.</p>
          <button type="button" onClick={() => onCreated(createdSet)} className="self-start rounded-lg bg-[rgb(var(--color-success))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-success))]/80">Done</button>
        </div>
      )}
    </div>
  )
}

export default function ScenarioSetBuilder({ fiscalPeriodId, fiscalPeriodLabel, wizardSessionId }) {
  const { user } = useAuth()
  const isCompAdmin = user?.user_type === 'comp_admin'
  // OI-ACTOR-SEPARATION-GLOBAL: currentUserId for self-approval gate
  const currentUserId = user?.user_id || user?.id || user?.email || null

  const [sets, setSets]               = useState([])
  const [count, setCount]             = useState(0)
  const [loading, setLoading]         = useState(false)
  const [error, setError]             = useState(null)
  const [showCreate, setShowCreate]   = useState(false)
  const [approvingId, setApprovingId] = useState(null)
  const [approveError, setApproveError] = useState(null)
  const [successMsg, setSuccessMsg]   = useState(null)
  const [periods, setPeriods]         = useState([])

  // Amendment state — governs approve only (create+definitions are not individually governed)
  const [amendModalOpen, setAmendModalOpen]   = useState(false)
  const [amendmentId, setAmendmentId]         = useState(null)
  const [pendingApproveId, setPendingApproveId] = useState(null)

  // S579 — sort
  const [sortCol, setSortCol] = useState('scenario_set_code')
  const [sortDir, setSortDir] = useState('asc')
  const toggleSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else { setSortCol(col); setSortDir('asc') }
  }
  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <ChevronsUpDown className="inline h-3 w-3 ml-0.5 text-[rgb(var(--color-text-muted))]" />
    return sortDir === 'asc'
      ? <ChevronUp className="inline h-3 w-3 ml-0.5 text-[rgb(var(--color-primary))]" />
      : <ChevronDown className="inline h-3 w-3 ml-0.5 text-[rgb(var(--color-primary))]" />
  }
  const sortedSets = useMemo(() => {
    return [...sets].sort((a, b) => {
      let av = '', bv = ''
      if (sortCol === 'scenario_set_code') { av = a.scenario_set_code ?? ''; bv = b.scenario_set_code ?? '' }
      else if (sortCol === 'scenario_set_name') { av = a.scenario_set_name ?? ''; bv = b.scenario_set_name ?? '' }
      else if (sortCol === 'period')  { av = a.period_label ?? a.period_code ?? String(a.fiscal_period_id ?? ''); bv = b.period_label ?? b.period_code ?? String(b.fiscal_period_id ?? '') }
      else if (sortCol === 'status')  { av = a.is_runnable ? '2' : a.approved_at ? '1' : '0'; bv = b.is_runnable ? '2' : b.approved_at ? '1' : '0' }
      else if (sortCol === 'approved_at') { av = a.approved_at ?? ''; bv = b.approved_at ?? '' }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [sets, sortCol, sortDir])

  useEffect(() => {
    fetch(`${API_BASE}/fiscal-periods?active_only=true`, { credentials: 'include' })
      .then((r) => r.json()).catch(() => ({}))
      .then((payload) => setPeriods(payload?.data ?? payload?.fiscal_periods ?? []))
  }, [])

  const loadSets = useCallback(async () => {
    setLoading(true); setError(null)
    const params = new URLSearchParams({ approved_only: 'false', active_only: 'false' })
    if (fiscalPeriodId) params.set('fiscal_period_id', String(fiscalPeriodId))
    try {
      const res = await fetch(`${API_BASE}/scenario-sets?${params.toString()}`, { credentials: 'include' })
      const payload = await res.json().catch(() => ({}))
      if (!res.ok) { setError(getErrorMessage(payload)); return }
      const rows = payload?.scenario_sets ?? payload?.data ?? []
      setSets(rows); setCount(payload?.count ?? rows.length)
    } catch (e) { setError(e.message || 'Network error.') }
    finally { setLoading(false) }
  }, [fiscalPeriodId])

  useEffect(() => { loadSets() }, [loadSets])

  // Amendment-aware approve
  const handleApproveClick = (scenarioSetId) => {
    // Amendment gate applies to post-approval modifications only.
    // First-time approval of a draft set is never an amendment action.
    runApprove(scenarioSetId, amendmentId || null)
  }

  const runApprove = async (scenarioSetId, amdId) => {
    setApprovingId(scenarioSetId); setApproveError(null); setSuccessMsg(null)
    try {
      const body = {}
      if (amdId) body.amendment_id = amdId
      const res = await fetch(`${API_BASE}/scenario-sets/${scenarioSetId}/approve`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const payload = await res.json().catch(() => ({}))
      if (!res.ok) { setApproveError(getErrorMessage(payload)); return }
      if (amdId) { await commitAmendment(amdId); setAmendmentId(null) }
      setSuccessMsg('Scenario set approved.')
      setSets((prev) => prev.map((s) => s.scenario_set_id === scenarioSetId ? { ...s, ...payload, approved_at: payload.approved_at } : s))
    } catch (e) { setApproveError(e.message || 'Network error.') }
    finally { setApprovingId(null) }
  }

  const handleAmendmentBegin = (newAmendmentId) => {
    setAmendmentId(newAmendmentId); setAmendModalOpen(false)
    if (pendingApproveId) { runApprove(pendingApproveId, newAmendmentId); setPendingApproveId(null) }
  }

  const handleCreated = () => {
    setShowCreate(false)
    setSuccessMsg('Scenario set created. Awaiting approval from a different comp_admin (NN-36).')
    loadSets()
  }

  return (
    <div className="flex flex-col gap-4">

      {/* Amendment Begin Modal */}
      <AmendmentBeginModal
        open={amendModalOpen}
        entityType={GOVERNED_ENTITY_TYPE}
        fiscalPeriodId={fiscalPeriodId ? Number(fiscalPeriodId) : null}
        wizardSessionId={wizardSessionId ?? null}
        onBegin={handleAmendmentBegin}
        onCancel={() => { setAmendModalOpen(false); setPendingApproveId(null) }}
      />

      <div>
        <p className="text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-primary))] mb-1">Scenarios</p>
        <h2 className="text-base font-bold text-[rgb(var(--color-text))]">Scenario Set Builder</h2>
        <p className="text-tok-sm text-[rgb(var(--color-text))] mt-0.5">
          {fiscalPeriodLabel ? `Scenario sets for ${fiscalPeriodLabel}. ` : 'Scenario sets across all periods. '}
          P50/P75/P90 definitions required. Actor separation enforced on approval (NN-36).
        </p>
      </div>

      {amendmentId && (
        <div className="rounded-lg bg-[rgb(var(--color-warning))]/10 border border-[rgb(var(--color-warning))]/30 px-3 py-2 text-tok-xs text-[rgb(var(--color-warning))] flex items-center justify-between">
          <span>⚠ Amendment open — scenario set changes are governed. Approve to commit.</span>
          <button type="button" onClick={async () => {
            try { await fetch(`${API_BASE}/amendments/${amendmentId}/cancel`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' } }) } catch (_) {}
            setAmendmentId(null)
          }} className="ml-3 text-tok-xs underline hover:no-underline">Cancel amendment</button>
        </div>
      )}
      {successMsg && <div className="rounded-lg bg-[rgb(var(--color-success))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-success))] flex items-center justify-between">{successMsg}<button type="button" onClick={() => setSuccessMsg(null)} className="ml-3 opacity-60 hover:opacity-100">✕</button></div>}
      {approveError && <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))] flex items-center justify-between">{approveError}<button type="button" onClick={() => setApproveError(null)} className="ml-3 opacity-60 hover:opacity-100">✕</button></div>}

      {showCreate && isCompAdmin && (
        <CreateWizard
          fiscalPeriodId={fiscalPeriodId}
          fiscalPeriodLabel={fiscalPeriodLabel}
          periods={periods}
          onCreated={handleCreated}
          onCancel={() => setShowCreate(false)}
          amendmentId={amendmentId}
        />
      )}

      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
            Scenario Sets {!loading && <span className="font-normal text-[rgb(var(--color-text))]">({count})</span>}
          </h3>
          {isCompAdmin && !showCreate && (
            <button type="button" onClick={() => { setShowCreate(true); setSuccessMsg(null) }} className="rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-primary-hover))]">+ New Set</button>
          )}
        </div>
        {error && <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{error}</div>}
        <div className="overflow-auto max-h-[calc(100vh-22rem)] rounded-xl border border-[rgb(var(--color-border))]">
          <table className="w-full text-tok-sm">
            <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))] z-10">
              <tr>
                {[['scenario_set_code','Code'],['scenario_set_name','Name'],['period','Period'],['definitions','Definitions'],['status','Status'],['approved_at','Approved At']].map(([col, label]) => (
                  <th key={col} className="px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] cursor-pointer select-none whitespace-nowrap hover:text-[rgb(var(--color-text))]" onClick={() => toggleSort(col)}>
                    {label}<SortIcon col={col} />
                  </th>
                ))}
                {isCompAdmin && <th className="px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] whitespace-nowrap">Action</th>}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={isCompAdmin ? 7 : 6} className="px-3 py-4 text-tok-sm text-[rgb(var(--color-text-muted))]">Loading scenario sets…</td></tr>}
              {!loading && sortedSets.length === 0 && <tr><td colSpan={isCompAdmin ? 7 : 6} className="px-3 py-4 text-tok-sm text-[rgb(var(--color-text-muted))]">No scenario sets found.</td></tr>}
              {!loading && sortedSets.map((set) => {
                const canApprove = isCompAdmin && !set.approved_at
                const isApproving = approvingId === set.scenario_set_id
                const codes = set.definition_codes ?? []
                const hasAllDefs = ['P50','P75','P90'].every((c) => codes.includes(c))
                // OI-ACTOR-SEPARATION-GLOBAL: disable Approve when current user created this set
                const setCreatedBy = set.created_by || set.created_by_user_id || null
                const isSelfApproval = !!(currentUserId && setCreatedBy && String(currentUserId) === String(setCreatedBy))
                return (
                  <tr key={set.scenario_set_id} className="border-t border-[rgb(var(--color-border))] hover:bg-[rgb(var(--color-surface-muted))]">
                    <td className="px-3 py-2.5 text-tok-xs font-mono font-medium text-[rgb(var(--color-text))] whitespace-nowrap">{fmt(set.scenario_set_code)}</td>
                    <td className="px-3 py-2.5 text-tok-sm text-[rgb(var(--color-text))] whitespace-nowrap">{fmt(set.scenario_set_name)}</td>
                    <td className="px-3 py-2.5 text-tok-sm text-[rgb(var(--color-text))] whitespace-nowrap">{set.period_label ?? set.period_code ?? fmt(set.fiscal_period_id)}</td>
                    <td className="px-3 py-2.5"><DefCodePills codes={codes} /></td>
                    <td className="px-3 py-2.5"><SetStatusBadge set={set} /></td>
                    <td className="px-3 py-2.5 text-tok-sm text-[rgb(var(--color-text-muted))] whitespace-nowrap">{fmtDateTime(set.approved_at)}</td>
                    {isCompAdmin && (
                      <td className="px-3 py-2.5">
                        {canApprove ? (
                          <div className="flex flex-col gap-1">
                            {isSelfApproval && (
                              <p className="text-tok-xs text-[rgb(var(--color-warning))]">⚠ You submitted this — a second comp admin must approve it.</p>
                            )}
                            <button type="button" onClick={() => handleApproveClick(set.scenario_set_id)} disabled={isApproving || !hasAllDefs || isSelfApproval} title={!hasAllDefs ? 'P50, P75, P90 definitions required before approval' : isSelfApproval ? 'You submitted this set — a second comp admin must approve it' : 'Approve (must be different user from creator)'} className="rounded-lg bg-[rgb(var(--color-success))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-success))]/80 disabled:opacity-50 disabled:cursor-not-allowed">
                              {isApproving ? 'Approving…' : 'Approve'}
                            </button>
                          </div>
                        ) : <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{set.approved_at ? 'Approved' : '—'}</span>}
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
